import React, { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import { Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import OrderDetailModal from "../common/OrderDetailModal";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, ChannelBadge, Loader } from "../reporting/ReportingShared";
import { getRedeemedRewardsList, getUserListing, getOrderListing } from "../../utils/apiCalls";

const inputStyle = {
    padding: "10px 14px", borderRadius: 10, fontSize: 13,
    border: `1px solid ${C.border}`, background: C.white,
    color: C.text, outline: "none", width: "100%",
};
const btnPrimary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", color: C.white,
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
};
const btnSecondary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
};

const rewardTypes = ["direct_cart", "voucher_self", "voucher", "customer_lookup"];
const channels = ["app", "pos"];
const sortableFields = ["createdAt", "productPrice", "channel", "rewardType"];
const sortOrders = ["ASC", "DESC"];

const rewardTypeMap = {
    direct_cart: { label: "Direct Cart", description: "Customer redeemed reward directly in their cart during order checkout in the app" },
    voucher_self: { label: "Self-Generated Voucher", description: "Customer generated a voucher code in the app for later redemption at POS" },
    voucher: { label: "Voucher Redemption", description: "Customer's voucher code was scanned and redeemed at the merchant's POS system" },
    customer_lookup: { label: "Customer Lookup", description: "POS staff looked up customer by phone number and redeemed reward via OTP verification" },
};
const getRewardTypeData = (t) => rewardTypeMap[t] || { label: t || "N/A", description: "No description available" };

const RewardTypeBadge = ({ t }) => {
    const colors = {
        direct_cart: { color: C.green, bg: C.greenLight },
        voucher_self: { color: C.blue, bg: C.blueLight },
        voucher: { color: C.purple, bg: C.purpleLight },
        customer_lookup: { color: C.orange, bg: C.orangeLight },
    };
    const c = colors[t] || { color: C.textMed, bg: C.borderLight };
    const { label, description } = getRewardTypeData(t);
    return (
        <span title={description}>
            <Badge color={c.color} bg={c.bg}>{label}</Badge>
        </span>
    );
};

const RedeemedRewards = () => {
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const [isLoading, setIsLoading] = useState(false);
    const [redeemedData, setRedeemedData] = useState([]);
    const [users, setUsers] = useState([]);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(10);
    const [tempLimit, setTempLimit] = useState(10);

    const initialFilters = {
        merchantId: selectedMerchant || "",
        userId: "", orderId: "", rewardType: "", channel: "",
        search: "", sortBy: "createdAt", order: "DESC",
        startDate: "", endDate: "",
    };
    const [filters, setFilters] = useState(initialFilters);
    const [tempFilters, setTempFilters] = useState(initialFilters);

    useEffect(() => {
        (async () => {
            const userList = await getUserListing({ limit: 1000 });
            const usersArray = userList?.users || [];
            setUsers(usersArray.filter((u) => u.userType === "Customer"));
        })();
    }, [userType]);

    const fetchData = useCallback(async (page = 1) => {
        setIsLoading(true);
        const params = {
            ...filters,
            merchantId: selectedMerchant && selectedMerchant !== "all" ? selectedMerchant : undefined,
        };
        const ResData = await getRedeemedRewardsList(page, limit, params);
        if (ResData) {
            setRedeemedData(ResData.redemptions || []);
            setTotalPages(ResData.pagination?.totalPages || 1);
        } else {
            setRedeemedData([]);
            setTotalPages(1);
        }
        setIsLoading(false);
    }, [filters, limit, selectedMerchant]);

    useEffect(() => { fetchData(currentPage); }, [fetchData, currentPage]);

    useEffect(() => {
        setFilters((prev) => ({ ...prev, merchantId: selectedMerchant || "" }));
        setTempFilters((prev) => ({ ...prev, merchantId: selectedMerchant || "" }));
        setCurrentPage(1);
    }, [selectedMerchant]);

    useEffect(() => {
        if (filterModalOpen) { setTempFilters(filters); setTempLimit(limit); }
    }, [filterModalOpen, filters, limit]);

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) setCurrentPage(page);
    };
    const applyFilters = () => {
        setFilters(tempFilters); setLimit(tempLimit); setCurrentPage(1); setFilterModalOpen(false);
    };
    const clearFilters = () => {
        const cleared = { ...initialFilters, merchantId: selectedMerchant || "" };
        setTempFilters(cleared); setFilters(cleared);
        setTempLimit(10); setLimit(10); setCurrentPage(1); setFilterModalOpen(false);
    };

    const handleOrderClick = async (orderId) => {
        if (!orderId) return;
        const ResData = await getOrderListing(1, 1, { orderId });
        if (ResData?.data?.orders?.length > 0) {
            setSelectedOrder(ResData.data.orders[0]);
            setOrderModalOpen(true);
        }
    };

    const tiles = useMemo(() => {
        const total = redeemedData.length;
        const totalValue = redeemedData.reduce((s, r) => s + (Number(r.productPrice) || 0), 0);
        const appCount = redeemedData.filter((r) => r.channel === "app").length;
        const posCount = redeemedData.filter((r) => r.channel === "pos").length;
        return { total, totalValue: totalValue.toFixed(2), appCount, posCount };
    }, [redeemedData]);

    const activeFilterPills = useMemo(() => {
        const pills = [];
        if (filters.userId) {
            const u = users.find((x) => x.id === parseInt(filters.userId));
            pills.push({ key: "userId", label: "User", value: u ? `${u.firstName} ${u.lastName}` : filters.userId });
        }
        if (filters.orderId) pills.push({ key: "orderId", label: "Order", value: filters.orderId });
        if (filters.rewardType) pills.push({ key: "rewardType", label: "Type", value: getRewardTypeData(filters.rewardType).label });
        if (filters.channel) pills.push({ key: "channel", label: "Channel", value: filters.channel.toUpperCase() });
        if (filters.search) pills.push({ key: "search", label: "Search", value: filters.search });
        if (filters.startDate) pills.push({ key: "startDate", label: "From", value: new Date(filters.startDate).toLocaleString() });
        if (filters.endDate) pills.push({ key: "endDate", label: "To", value: new Date(filters.endDate).toLocaleString() });
        return pills;
    }, [filters, users]);

    const removeFilter = (key) => {
        const next = { ...filters, [key]: "" };
        setFilters(next); setCurrentPage(1);
    };

    const renderPagination = () => {
        const pages = [];
        const maxPagesToShow = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        for (let i = startPage; i <= endPage; i++) pages.push(i);
        return pages;
    };
    const pageBtnStyle = (active, disabled) => ({
        minWidth: 36, height: 36, padding: "0 12px", borderRadius: 8,
        border: `1px solid ${active ? C.coral : C.border}`,
        background: active ? `linear-gradient(135deg, ${C.coral}, #00bfa5)` : C.white,
        color: active ? C.white : C.textMed,
        fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1, transition: "all 0.15s",
    });

    return (
        <Fragment>
            <Breadcrumb title="Redeemed Rewards" parent="Loyalty" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    {/* Tiles */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile label="Redemptions (page)" value={tiles.total} sub={`Page ${currentPage} of ${totalPages}`} icon="🎁" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                        <Tile label="Total Value" value={`${tiles.totalValue} SAR`} sub="Sum of redeemed product prices" icon="💰" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={80} />
                        <Tile label="App Channel" value={tiles.appCount} sub="In-app redemptions" icon="📱" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={160} />
                        <Tile label="POS Channel" value={tiles.posCount} sub="At point of sale" icon="🏪" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={240} />
                    </div>

                    {/* Table */}
                    <RCard
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <span>🎁 Redeemed Rewards <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {tiles.total} on this page</span></span>
                                <button onClick={() => setFilterModalOpen(true)} style={btnPrimary}>
                                    Filters {activeFilterPills.length > 0 && <span style={{ background: "rgba(255,255,255,0.25)", padding: "2px 8px", borderRadius: 10, marginLeft: 6, fontSize: 11 }}>{activeFilterPills.length}</span>}
                                </button>
                            </div>
                        }
                    >
                        {activeFilterPills.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                                {activeFilterPills.map((p) => (
                                    <span
                                        key={p.key}
                                        onClick={() => removeFilter(p.key)}
                                        style={{
                                            display: "inline-flex", alignItems: "center", gap: 6,
                                            padding: "5px 10px", borderRadius: 8, fontSize: 12,
                                            background: C.coralLight, color: C.coralDark, fontWeight: 600,
                                            cursor: "pointer", border: `1px solid #e0f2f1`,
                                        }}
                                        title="Click to remove"
                                    >
                                        <strong>{p.label}:</strong> {p.value} <span style={{ opacity: 0.6 }}>×</span>
                                    </span>
                                ))}
                            </div>
                        )}

                        {isLoading ? (
                            <Loader />
                        ) : redeemedData.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>No redemptions found.</div>
                        ) : (
                            <>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={tbl.table}>
                                        <thead>
                                            <tr>
                                                <th style={tbl.th}>ID</th>
                                                <th style={tbl.th}>Code</th>
                                                <th style={tbl.th}>Order</th>
                                                <th style={tbl.th}>User</th>
                                                <th style={tbl.th}>Merchant</th>
                                                <th style={tbl.th}>Product</th>
                                                <th style={tbl.th}>Price</th>
                                                <th style={tbl.th}>Channel</th>
                                                <th style={tbl.th}>Reward Type</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {redeemedData.map((item) => (
                                                <tr key={item.id} className="rpt-row">
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>#{item.id}</td>
                                                    <td style={{ ...tbl.td, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, fontWeight: 700 }}>
                                                        {item.voucher?.code || <span style={{ color: C.textLight, fontFamily: "inherit", fontWeight: "normal" }}>—</span>}
                                                    </td>
                                                    <td style={tbl.td}>
                                                        {item.orderId ? (
                                                            <span
                                                                style={{ cursor: "pointer", color: C.coral, fontWeight: 700, fontSize: 12 }}
                                                                onClick={() => handleOrderClick(item.orderId)}
                                                                title="Click to view order"
                                                            >
                                                                #{item.orderId}
                                                            </span>
                                                        ) : <span style={{ color: C.textLight }}>—</span>}
                                                    </td>
                                                    <td style={{ ...tbl.td, fontWeight: 600 }}>{item.user?.name || "—"}</td>
                                                    <td style={{ ...tbl.td, color: C.textMed }}>{item.merchant?.name || "—"}</td>
                                                    <td style={tbl.td}>{item.product?.name || "—"}</td>
                                                    <td style={{ ...tbl.td, fontWeight: 600 }}>
                                                        {item.productPrice ? `${item.productPrice} SAR` : <span style={{ color: C.textLight }}>—</span>}
                                                    </td>
                                                    <td style={tbl.td}>{item.channel ? <ChannelBadge c={item.channel} /> : <span style={{ color: C.textLight }}>—</span>}</td>
                                                    <td style={tbl.td}><RewardTypeBadge t={item.rewardType} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 24 }}>
                                        <button style={pageBtnStyle(false, currentPage <= 1)} disabled={currentPage <= 1} onClick={() => handlePageChange(currentPage - 1)}>‹</button>
                                        {renderPagination().map((p) => (
                                            <button key={p} style={pageBtnStyle(p === currentPage, false)} onClick={() => handlePageChange(p)}>{p}</button>
                                        ))}
                                        <button style={pageBtnStyle(false, currentPage >= totalPages)} disabled={currentPage >= totalPages} onClick={() => handlePageChange(currentPage + 1)}>›</button>
                                    </div>
                                )}
                            </>
                        )}
                    </RCard>
                </div>
            </Container>

            <OrderDetailModal
                selectedOrder={selectedOrder}
                orderModalOpen={orderModalOpen}
                setOrderModalOpen={setOrderModalOpen}
            />

            {/* Filter Modal */}
            <Modal isOpen={filterModalOpen} toggle={() => setFilterModalOpen(false)}>
                <ModalHeader toggle={() => setFilterModalOpen(false)}>Filter Redeemed Rewards</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>USER</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.userId} onChange={(e) => setTempFilters({ ...tempFilters, userId: e.target.value })}>
                                <option value="">All Users</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.phoneNo})</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>ORDER ID</Label>
                            <Input type="text" style={inputStyle} value={tempFilters.orderId} onChange={(e) => setTempFilters({ ...tempFilters, orderId: e.target.value })} placeholder="Enter Order ID" />
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>REWARD TYPE</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.rewardType} onChange={(e) => setTempFilters({ ...tempFilters, rewardType: e.target.value })}>
                                <option value="">All Types</option>
                                {rewardTypes.map((t) => (
                                    <option key={t} value={t}>{getRewardTypeData(t).label}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>CHANNEL</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.channel} onChange={(e) => setTempFilters({ ...tempFilters, channel: e.target.value })}>
                                <option value="">All Channels</option>
                                {channels.map((c) => (<option key={c} value={c}>{c.toUpperCase()}</option>))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>SEARCH</Label>
                            <Input type="text" style={inputStyle} value={tempFilters.search} onChange={(e) => setTempFilters({ ...tempFilters, search: e.target.value })} placeholder="Search…" />
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>SORT BY</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.sortBy} onChange={(e) => setTempFilters({ ...tempFilters, sortBy: e.target.value })}>
                                {sortableFields.map((f) => (<option key={f} value={f}>{f}</option>))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>ORDER</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.order} onChange={(e) => setTempFilters({ ...tempFilters, order: e.target.value })}>
                                {sortOrders.map((o) => (<option key={o} value={o}>{o}</option>))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>START DATE</Label>
                            <Input type="datetime-local" style={inputStyle} value={tempFilters.startDate} onChange={(e) => setTempFilters({ ...tempFilters, startDate: e.target.value })} />
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>END DATE</Label>
                            <Input type="datetime-local" style={inputStyle} value={tempFilters.endDate} onChange={(e) => setTempFilters({ ...tempFilters, endDate: e.target.value })} />
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>PER PAGE</Label>
                            <Input type="select" style={inputStyle} value={tempLimit} onChange={(e) => setTempLimit(parseInt(e.target.value))}>
                                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                            </Input>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={clearFilters} style={btnSecondary}>Clear</button>
                    <button onClick={applyFilters} style={btnPrimary}>Apply</button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default RedeemedRewards;
